import { t } from '../lib/i18n';

// Service Worker 通知相关接口定义
interface ExtendedNotificationOptions {
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  vibrate?: number[];
  requireInteraction?: boolean;
  silent?: boolean;
  debug?: boolean;
  customTitle?: string;
  customBody?: string;
  [key: string]: any; // 允许其他属性
}

// 使用 Service Worker 发送通知
const sendNotificationViaServiceWorker = async (title: string, options: ExtendedNotificationOptions = {}): Promise<boolean> => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    console.log('Service Worker is not supported in this browser');
    return false;
  }
  
  try {
    // 确保服务工作器已注册并准备好
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (!registration) {
      console.log('No Service Worker registration found');
      return false;
    }
    
    console.log('Service Worker ready for notification');
    
    // 设置默认通知选项
    const notificationOptions = {
      icon: '/icons/app/android-chrome-192x192.png',
      badge: '/favicon.ico',
      vibrate: [100, 50, 100],
      requireInteraction: true,
      ...options
    };
    
    // 显示通知
    await registration.showNotification(title, notificationOptions);
    
    console.log('Service Worker notification sent with options:', notificationOptions);
    return true;
  } catch (error) {
    console.error('Failed to show notification via Service Worker:', error);
    return false;
  }
};

// 检查浏览器是否支持通知
export const isNotificationSupported = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

// 获取当前通知权限状态
export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
};

// 请求通知权限
export const requestNotificationPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
  if (!isNotificationSupported()) {
    console.log('通知在此浏览器中不受支持');
    return 'unsupported';
  }
  
  try {
    const permission = await Notification.requestPermission();
    console.log('通知权限请求结果:', permission);
    return permission;
  } catch (error) {
    console.error('请求通知权限时出错:', error);
    return Notification.permission;
  }
};

// 显示番茄钟通知
export const showPomodoroNotification = async (
  mode: 'work' | 'break',
  options?: {
    debug?: boolean;
    customTitle?: string;
    customBody?: string;
  }
): Promise<boolean> => {
  const opts = options || {};
  const debug = opts.debug || false;
  
  try {
    if (!isNotificationSupported()) {
      console.log('通知在此浏览器中不受支持');
      return false;
    }
    
    if (debug) {
      console.log('---NOTIFICATION DEBUG---');
      console.log('Browser:', navigator.userAgent);
      console.log('Notification permission:', Notification.permission);
      console.log('Page visibility:', document.visibilityState);
      console.log('----------------------');
    }
    
    // 准备通知内容
    const title = opts.customTitle || (mode === 'work' ? t('休息时间到!', 'Time for a break!') : t('专注时间到!', 'Time to focus!'));
    const body = opts.customBody || (mode === 'work' 
      ? t('工作阶段已完成，现在是休息时间。', 'Focus session done. Take a break.')
      : t('休息结束，开始新的专注时间。', 'Break is over. Time for a new focus session.'));
    
    // 确保首先获取权限
    const permission = getNotificationPermission();
    
    if (permission !== 'granted') {
      if (permission === 'denied') {
        console.log('通知权限被拒绝');
        return false;
      }
      
      // 请求权限
      const newPermission = await requestNotificationPermission();
      if (newPermission !== 'granted') {
        console.log('通知权限请求被拒绝');
        return false;
      }
    }
    
    // 直接使用 Notification API 发送通知
    try {
      const notification = new Notification(title, {
        body: body,
        icon: '/icons/app/android-chrome-192x192.png',
        badge: '/favicon.ico',
        tag: `pomodoro-${Date.now()}`,
        requireInteraction: true,
        silent: false
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      
      console.log('普通通知发送成功');
      return true;
    } catch (directError) {
      console.error('普通通知发送失败:', directError);
      
      // 尝试通过 Service Worker 发送通知
      if ('serviceWorker' in navigator) {
        try {
          const result = await sendNotificationViaServiceWorker(title, {
            body: body,
            tag: `pomodoro-${Date.now()}`,
            requireInteraction: true,
            data: {
              url: window.location.href,
              mode: mode
            }
          });
          
          if (result) {
            console.log('通知已通过 Service Worker 发送');
            return true;
          }
        } catch (swError) {
          console.error('Service Worker 通知发送失败:', swError);
        }
      }
    }
    
    // 如果所有方法都失败，尝试使用警报
    if (document.visibilityState !== 'visible') {
      setTimeout(() => {
        alert(`${title}\n${body}`);
      }, 100);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('显示通知时发生错误:', error);
    
    // 最后的后备方案
    try {
      alert(t(`通知\n时间已到，请查看您的番茄钟状态。`, `Notification\nTime's up. Check your Pomodoro.`));
      return true;
    } catch (e) {
      return false;
    }
  }
};

// 测试通知功能
export const testNotification = async (debug: boolean = true, options?: { 
  isEnablingNotification?: boolean
}): Promise<boolean> => {
  try {
    if (!isNotificationSupported()) {
      console.log('通知在此浏览器中不受支持');
      alert(t('您的浏览器不支持通知功能。', "Your browser doesn't support notifications."));
      return false;
    }
    
    // 检查通知权限
    let permission = getNotificationPermission();
    
    if (permission !== 'granted') {
      console.log('正在请求通知权限...');
      permission = await requestNotificationPermission();
      
      if (permission !== 'granted') {
        console.log('通知权限被拒绝');
        alert(t('通知权限被拒绝，请在浏览器设置中允许通知。', 'Notification permission was denied. Allow notifications in your browser settings.'));
        return false;
      }
    }
    
    console.log('尝试发送测试通知...');
    
    const isEnabling = options?.isEnablingNotification || false;
    const title = isEnabling ? t('通知已启用', 'Notifications enabled') : t('测试通知', 'Test notification');
    const body = isEnabling 
      ? t('您将在番茄钟结束时收到通知。', "You'll be notified when a Pomodoro ends.")
      : t('这是一条测试通知，如果您看到此消息，则通知功能正常。', 'This is a test notification. If you can see it, notifications are working.');
    
    // 直接使用简单的 Notification API
    try {
      const notification = new Notification(title, {
        body: body,
        icon: '/icons/app/android-chrome-192x192.png',
        requireInteraction: false
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      
      // 5秒后自动关闭通知
      setTimeout(() => {
        notification.close();
      }, 5000);
      
      console.log('测试通知发送成功');
      return true;
    } catch (error) {
      console.error('测试通知发送失败，尝试使用 Service Worker:', error);
      
      // 如果直接通知失败，尝试用 Service Worker
      return await showPomodoroNotification('work', {
        debug: true,
        customTitle: title,
        customBody: body
      });
    }
  } catch (error) {
    console.error('测试通知时出错:', error);
    return false;
  }
}; 